<script lang="ts">
  import { onDestroy, onMount, untrack } from 'svelte';
  import { findPath } from '../../game/geometry';
  import type { ServerMessage } from '../../shared/protocol';
  import { isUntimedMode } from '../../shared/modes';
  import { chaosBoardState } from './chaos';
  import { localSubmission, tentativeScore } from './local-verdict';
  import { getSession } from '../session/context';
  import { Sfx } from '../audio/sounds';
  import type { GameplayPerformance } from './gameplay-performance';
  import { Clock } from '../platform/clock.svelte';
  import { getAppearance } from '../themes/appearance.svelte';
  import { roundClock } from './round-clock';
  import { ScoreTally } from './score-tally.svelte';
  import { WordFeedback } from './word-feedback.svelte';
  import ChaosStatus from './ChaosStatus.svelte';
  import PowerUpFeedback from './PowerUpFeedback.svelte';
  import CountdownOverlay from './CountdownOverlay.svelte';
  import GameBoard from './GameBoard.svelte';

  interface Props {
    gameplayPerformance: GameplayPerformance | undefined;
    /** Root-owned so one-shot round cues survive Settings and screen remounts. */
    lastCountdown: number;
    visualRoundId: string | null;
    canPlay?: boolean;
    finishing?: boolean;
  }
  let {
    gameplayPerformance,
    lastCountdown = $bindable(),
    visualRoundId = $bindable(),
    canPlay = $bindable(false),
    finishing = false,
  }: Props = $props();
  const session = getSession();

  const SCORE_GAIN_MS = 1_000;

  // Pause the page-owned monitor without resetting its sample or report budget.
  onDestroy(() => gameplayPerformance?.setActive(false));

  const clock = new Clock();
  onMount(() => clock.mount());

  let selectedPath = $state<number[]>([]);
  let gameBoard: GameBoard | undefined;
  let powerUpFeedback = $state<PowerUpFeedback>();
  const feedback = new WordFeedback(() => selectedPath.length > 0);
  onDestroy(() => feedback.dispose());

  let scoreGain = $state(0);
  /** Restarts the pop and gain cues per acceptance; the count-up itself is continuous. */
  let scoreUpdate = $state(0);
  let clearScoreTimer = 0;
  let scoreRoundId: string | null = null;
  const appearance = getAppearance();
  const tally = new ScoreTally({ motionAllowed: () => appearance.motionAllowed });
  onDestroy(() => tally.dispose());
  // Confirmed words plus presumed ones still awaiting the server, so a filter hit counts at once.
  const score = $derived(tentativeScore($session.found, $session.submissions, $session.roundId));
  $effect(() => {
    if (scoreRoundId !== $session.roundId) {
      scoreRoundId = $session.roundId;
      scoreGain = 0;
      scoreUpdate = 0;
      tally.reset();
      window.clearTimeout(clearScoreTimer);
    }
  });
  // Any accepted total — live verdicts and recovered snapshots alike — counts up. `retarget`
  // reads the displayed value, which must not restart the count on every tick.
  $effect(() => {
    const target = score;
    untrack(() => tally.retarget(target));
  });
  onDestroy(() => window.clearTimeout(clearScoreTimer));

  const currentSize = $derived($session.activeConfig?.boardSize ?? $session.config.boardSize);
  const currentMinLength = $derived($session.activeConfig?.minWordLength ?? $session.config.minWordLength);
  const serverNow = $derived(clock.now + $session.serverOffset);
  const untimed = $derived(isUntimedMode($session.mode));
  const timeView = $derived(
    roundClock({
      mode: $session.mode,
      dailyDate: $session.dailyDate,
      endAt: $session.endAt,
      endlessBoard: $session.endlessBoard,
      now: serverNow,
    }),
  );
  const clockNeeded = $derived(
    clock.visible && ($session.phase === 'countdown' || ($session.phase === 'playing' && timeView.ticking)),
  );
  $effect(() => clock.run(clockNeeded));
  const countdownNumber = $derived(Math.max(1, Math.ceil(($session.startAt - serverNow) / 1000)));
  // Play does not wait for the socket: words entered during a blip queue in the session
  // and the server judges their timing after the recovery snapshot.
  $effect(() => {
    canPlay =
      $session.joined &&
      !$session.spectator &&
      !$session.finished &&
      !finishing &&
      $session.phase === 'playing' &&
      $session.board !== null &&
      timeView.open;
  });
  $effect(() => gameplayPerformance?.setActive(canPlay));
  const currentWord = $derived(selectedPath.map((index) => $session.board?.[index] ?? '').join(''));
  // Chaos: the schedule and my effects come from the snapshot; the clock decides what shows now.
  const chaos = $derived(
    chaosBoardState({
      config: $session.activeConfig,
      powerUps: $session.powerUps,
      effects: $session.effects,
      now: serverNow,
      canPlay,
    }),
  );
  const hintsAvailable = $derived($session.mode === 'practice' || chaos.active);
  const playerName = (id: string) => $session.players.find((player) => player.id === id)?.name ?? 'Someone';
  // A new selection replaces any lingering result/feedback for the previous word; the first
  // touch on an Endless board starts that board's clock.
  $effect(() => {
    if (selectedPath.length) feedback.clear();
    if (selectedPath.length === 1) session.markBoardActive();
  });
  // The muted pending word resolves visually once its verdict lands, whether that verdict
  // arrives as a reply or is recovered from a snapshot after a reconnect. A presumed word
  // already shows as accepted; only a reversal changes it.
  $effect(() => {
    if (feedback.kind !== 'pending' && feedback.kind !== 'accepted') return;
    const entry = $session.submissions.find((entry) => entry.id === feedback.pendingSubmissionId);
    if (!entry || entry.pending) return;
    if (entry.round !== $session.roundId || !canPlay || selectedPath.length) return;
    if (feedback.kind === 'accepted' && entry.outcome === 'accepted') return;
    feedback.show(
      entry.word,
      entry.outcome === 'accepted' ? 'accepted' : entry.outcome === 'duplicate' ? 'duplicate' : 'shake',
      entry.status,
    );
  });
  $effect(() => {
    // Root-owned cue bookkeeping is written here, so read it without subscribing to it.
    if (
      $session.phase === 'countdown' &&
      $session.roundId &&
      countdownNumber !== untrack(() => lastCountdown)
    ) {
      lastCountdown = countdownNumber;
      Sfx.play('countdown');
    } else if ($session.phase !== 'countdown') {
      lastCountdown = 0;
    }
  });
  $effect(() => {
    if (
      !untimed &&
      $session.phase === 'playing' &&
      $session.roundId &&
      !timeView.open &&
      untrack(() => visualRoundId) !== $session.roundId
    ) {
      visualRoundId = $session.roundId;
      clearSelection();
      feedback.show("time's up!");
      Sfx.play('end');
    }
  });

  export function handleMessage(message: ServerMessage, submissionFeedback?: 'accept' | 'reject'): void {
    switch (message.t) {
      case 'wordResult': {
        if (message.ok && submissionFeedback === 'accept' && message.roundId === $session.roundId)
          showScoreGain(message.pts);
        return;
      }
      case 'powerUp':
        if (message.roundId === $session.roundId && canPlay)
          powerUpFeedback?.show(message, $session.meId, playerName);
        return;
      case 'hint':
        if (message.roundId !== $session.roundId || !hintsAvailable || !canPlay) return;
        if (!message.w) {
          feedback.show('You found every word!');
          return;
        }
        selectedPath = findPath($session.board ?? [], message.w) ?? [];
        return;
    }
  }

  export function clearSelection(): void {
    gameBoard?.reset();
    selectedPath = [];
    feedback.clear();
  }

  function submitWord(word: string, path: readonly number[] = [], pickupIds: readonly string[] = []): void {
    clearSelection();
    feedback.show(word);
    const roundId = $session.roundId;
    const local = localSubmission({
      word,
      canPlay: canPlay && roundId !== null,
      minLength: currentMinLength,
      found: $session.found,
      pending: $session.submissions,
      filter: $session.solutionFilter,
      effects: $session.effects,
      now: serverNow,
    });
    if (local.kind === 'closed' || !roundId) {
      showMessage('Round is not accepting words');
      return;
    }
    if (local.kind === 'reject') {
      rejectWord(local.message, word, local.feedback);
      return;
    }
    if (local.kind === 'pending') {
      feedback.show(word, 'pending');
      feedback.pendingSubmissionId = local.submissionId;
      return;
    }
    const { presumedPoints } = local;
    const submission = session.submitWord(word, path, pickupIds, presumedPoints);
    if (submission === 'blocked') {
      showMessage('Too many words waiting');
      return;
    }
    if (presumedPoints === undefined) feedback.show(word, 'pending');
    else {
      Sfx.play('accept');
      feedback.show(word, 'accepted', 'Accepted');
      showScoreGain(presumedPoints);
    }
    feedback.pendingSubmissionId = $session.submissions.at(-1)?.id ?? null;
    if (submission === 'sent') gameplayPerformance?.submitted(roundId, word);
    gameplayPerformance?.submissionFeedback();
  }

  function showScoreGain(points: number): void {
    window.clearTimeout(clearScoreTimer);
    scoreGain = points;
    scoreUpdate += 1;
    clearScoreTimer = window.setTimeout(() => (scoreGain = 0), SCORE_GAIN_MS);
  }

  function rejectWord(message: string, word = '', kind: 'shake' | 'duplicate' = 'shake'): void {
    Sfx.play(kind === 'duplicate' ? 'duplicate' : 'reject');
    feedback.show(word, kind, message);
  }

  export function showHint(): void {
    if (canPlay && $session.mode === 'practice' && $session.roundId) session.requestHint();
  }

  export function showMessage(message: string): void {
    feedback.show(currentWord || feedback.message, 'shake', message);
  }
</script>

<section id="screen-game" class="screen">
  {#if $session.spectator}<p class="status">Watching this round — you’ll play in the next one.</p>{/if}
  <div class="board-stage">
    <div class="game-stack">
      <div class="hud">
        <div class="hud-panel time-block">
          <p class="game-mode-label">{timeView.label}</p>
          <div class:urgent={timeView.urgent} class="timer" aria-label={timeView.ariaLabel}>
            {timeView.value}
          </div>
        </div>
        <div class="hud-panel count-block">
          {#key scoreUpdate}<p class="score-gain" class:rising={scoreGain > 0} aria-hidden="true">
              {scoreGain ? `+${scoreGain}` : '\u00a0'}
            </p>{/key}
          <output
            class="tentative-score"
            aria-label="Tentative score"
            title="Raw points before uniqueness bonus"
          >
            {tally.displayed}
          </output>
        </div>
      </div>
      {#if chaos.active}<ChaosStatus effects={chaos.effects} shield={$session.shield} />{/if}
      <GameBoard
        bind:this={gameBoard}
        board={$session.board}
        size={currentSize}
        revealed={$session.board !== null && $session.phase === 'playing'}
        enabled={canPlay && !chaos.frozen}
        powerUps={chaos.spawns}
        spun={chaos.spun}
        fogged={chaos.fogged}
        frozen={canPlay && chaos.frozen}
        bind:selectedPath
        submit={submitWord}
        observeInput={(timestamp) => gameplayPerformance?.input(timestamp)}
      >
        {#snippet header()}
          {#key $session.roundId}{#if canPlay}<PowerUpFeedback bind:this={powerUpFeedback} />{/if}{/key}
          <div
            class:pending={feedback.muted}
            class:shake={feedback.kind === 'shake' || feedback.kind === 'duplicate'}
            class:duplicate={feedback.kind === 'duplicate'}
            class:accepted={feedback.kind === 'accepted'}
            class="current"
          >
            <span class="word-display motion-settles">{currentWord || feedback.message || '\u00a0'}</span>
          </div>
          <span class="feedback-announcement" role="status">{feedback.error}</span>
        {/snippet}
      </GameBoard>
    </div>
  </div>
  {#if $session.phase === 'countdown'}<CountdownOverlay number={countdownNumber} />{/if}
</section>

<style>
  #screen-game {
    --hud-size: clamp(48px, 14vw, 64px);
    --word-size: clamp(32px, 9vw, 36px);
    --game-gap: 16px;
    --hud-height: calc(var(--hud-size) + 18px);
    --stack-overhead: calc(var(--hud-height) + var(--game-gap));
    gap: 8px;
    /* Nothing here is meant to be copied; a selection gesture escaping the board must find
       no selectable text (timer, score, current word) to highlight on iOS. */
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }
  .hud {
    min-height: var(--hud-height);
    width: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px;
    flex: 0 0 auto;
  }
  .timer,
  .tentative-score {
    font-size: var(--hud-size);
    font-weight: 700;
    color: var(--theme-accent);
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }
  .timer {
    min-width: 4.2ch;
    padding-inline-start: 6px;
    transform-origin: left center;
  }
  .tentative-score {
    text-align: right;
    padding-inline-end: 6px;
  }
  .score-gain {
    text-align: right;
    padding-inline-end: 6px;
    color: var(--theme-success);
  }
  /* Runs for SCORE_GAIN_MS: one rise, then fades before the label clears. */
  .score-gain.rising {
    animation: score-gain-rise 1000ms ease-out both;
  }
  @keyframes score-gain-rise {
    0% {
      opacity: 0;
      transform: translateY(5px);
    }
    12% {
      opacity: 1;
      transform: none;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
  .hud-panel {
    background: var(--theme-hud-background);
    border-radius: var(--theme-input-radius);
    min-height: var(--hud-height);
    flex: 1 1 0;
    min-width: 0;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  .timer.urgent {
    color: var(--theme-danger);
    animation: var(--theme-urgent-animation);
  }

  .board-stage {
    display: flex;
    align-items: safe flex-end;
    overflow-y: auto;
    justify-content: center;
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    container-type: size;
  }
  .game-stack {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    gap: var(--game-gap);
    width: 100%;
  }
  .feedback-announcement {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .current {
    height: calc(var(--word-size) * 1.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--word-size);
    line-height: 1.2;
    flex: 0 0 auto;
    width: 100%;
  }
  .current > span {
    max-width: 100%;
    overflow-x: auto;
  }
  /* Awaiting a verdict longer than a moment: static muting, so it reads the same with reduced motion. */
  .current.pending > span {
    color: var(--theme-muted-text);
  }
  .current.accepted {
    --settle-animation: var(--theme-accept-animation);
  }
  .current.shake {
    --settle-animation: var(--theme-reject-animation);
    text-decoration: underline wavy;
  }
  .current.duplicate {
    --settle-animation: var(--theme-duplicate-animation);
  }

  .game-mode-label {
    padding-inline-start: 6px;
  }
  .game-mode-label,
  .score-gain {
    font-size: 11px;
    line-height: 14px;
    margin: 0 0 4px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  @media (max-height: 560px) {
    #screen-game {
      --hud-size: 40px;
      --word-size: 36px;
      --game-gap: 8px;
    }
  }
</style>
